import React, { useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon } from '@heroicons/react/20/solid';
import Drawer from '@ui/drawer/Drawer';
import useDrawer from '@ui/drawer/DrawerRef';
import Dialogue from '@ui/dialog/Dialogue';
import useDialogue from '@ui/dialog/DialogueRef';
import FormButton from '@ui/button/FormButton';
import FormInputText from '@ui/form/input/FormInputText';
import ButtonLinkList from '@ui/button/ButtonLinkList';
import GroupBoard, { GroupBoardGroup } from '@ui/board/GroupBoard';
import Message from '@ui/message/Message';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { ChannelGroups, ChannelListItem } from '@app/model/bi/Bi';
import { useSaveChannelGroups } from '@app/model/bi/service/BiHooks';
import { formatDate } from '../Bi.utilities';

export type ChannelGroupsDrawerHandle = { open: () => void };

type Props = {
	data: ChannelGroups,
	companyName: string,
	// All properties of the company, also the properties that the user cannot see: the groups belong to the company (D1).
	propertyCount: number,
};

const NOT_GROUPED = 'not-grouped';
const number = new Intl.NumberFormat('en-GB');
const euro = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

// A group in the editor: key = the board id (the saved id as text, or "new-…"); id = the saved id, null for a new group.
type DraftGroup = { key: string, id: number | null, title: string, keys: string[] };

const sameGroups = (draft: DraftGroup[], saved: ChannelGroups['groups']) => draft.length === saved.length && draft.every((g, i) =>
	g.id === saved[i].id && g.title === saved[i].title && g.keys.length === saved[i].keys.length && g.keys.every(k => saved[i].keys.includes(k)));

// The groups editor of the Channels report (RQ-02 … RQ-05, grill D1, Q2–Q5): the user drags each channel of the company into
// a group of the company. One card per channel key (D2: the names that differ only in capitals or spaces are one card, with
// the number of spellings). The cards show the bookings made in the last 12 months, all stay dates, sold now (Q4), largest
// first; a channel with none shows 0 and stays in the list. "New" (Q5): a channel in no group with its first booking after
// the last save. The editor starts from the saved groups; a company with no group starts empty (Q3, as ROO2-244). The
// changes wait for Save groups; with changes, each close action asks first.
const ChannelGroupsDrawer = React.forwardRef<ChannelGroupsDrawerHandle, Props>(({ data, companyName, propertyCount }, ref) => {
	const drawer = useDrawer();
	const nameDialogue = useDialogue<string | null>();
	const deleteDialogue = useDialogue<string>();
	const closeDialogue = useDialogue<void>();
	const toaster = useToaster();
	const [groups, setGroups] = useState<DraftGroup[]>([]);
	const [editing, setEditing] = useState<string | null>(null);
	const nameForm = useForm<{ title: string }>({ defaultValues: { title: '' } });
	const save = useSaveChannelGroups(
		() => {
			toaster.success('Channel groups', 'RoomPulse saved the groups. The Channels report of all properties uses them now.');
			drawer.close();
		},
		error => toaster.error('Channel groups', error),
	);

	useImperativeHandle(ref, () => ({
		open: () => {
			setGroups(data.groups.map(g => ({ key: String(g.id), id: g.id, title: g.title, keys: [...g.keys] })));
			drawer.open();
		},
	}));

	const changed = !sameGroups(groups, data.groups);
	const requestClose = () => changed ? closeDialogue.open() : drawer.close();
	const byKey = useMemo(() => new Map(data.channels.map(c => [c.key, c])), [data.channels]);
	const savedDay = data.savedAt?.slice(0, 10) ?? null;
	const isNew = (c: ChannelListItem, grouped: boolean) => !grouped && savedDay !== null && c.firstBooking !== null && c.firstBooking > savedDay;
	const placed = new Set(groups.flatMap(g => g.keys));
	// "No channel" (the nights with no name) has no card: it is never in a group.
	const channels = data.channels.filter(c => c.key !== '');
	const loose = channels.filter(c => !placed.has(c.key));
	const item = (c: ChannelListItem, grouped: boolean) => ({
		id: c.key, label: c.title, tag: isNew(c, grouped) ? 'New' : undefined,
		meta: `${c.spellings.length > 1 ? `${c.spellings.length} spellings · ` : ''}${c.properties} ${c.properties === 1 ? 'property' : 'properties'} · ${number.format(c.roomNights)} RN · ${euro.format(c.revenue)}`,
	});
	const totalOf = (keys: string[]) => keys.reduce((s, k) => ({ rn: s.rn + (byKey.get(k)?.roomNights ?? 0), rev: s.rev + (byKey.get(k)?.revenue ?? 0) }), { rn: 0, rev: 0 });
	const newCount = loose.filter(c => isNew(c, false)).length;

	const actions = (group: DraftGroup) => <ButtonLinkList variant="actions" ariaLabel={`Actions of the group ${group.title}`} items={[
		{ name: 'Rename', onClick: () => { setEditing(group.key); nameForm.reset({ title: group.title }); nameDialogue.open(group.key); } },
		{ name: 'Delete', tone: 'danger', onClick: () => { setEditing(group.key); deleteDialogue.open(group.key); } },
	]} />;

	const board: GroupBoardGroup[] = [
		{
			id: NOT_GROUPED, title: 'Not grouped', tone: 'attention', items: loose.map(c => item(c, false)),
			subtitle: loose.length === 0 ? 'Every channel has a group' : `${loose.length} channels · each one is its own channel in the report`,
		},
		...groups.map(g => {
			const t = totalOf(g.keys);
			return {
				id: g.key, title: g.title, actions: actions(g),
				items: [...g.keys].map(k => byKey.get(k)).filter((c): c is ChannelListItem => c !== undefined).sort((a, b) => b.revenue - a.revenue).map(c => item(c, true)),
				subtitle: `${number.format(t.rn)} RN · ${euro.format(t.rev)} · last 12 months`,
			};
		}),
	];

	const move = (key: string, to: string) => setGroups(current => current.map(g => ({
		...g,
		keys: g.key === to ? [...g.keys.filter(k => k !== key), key] : g.keys.filter(k => k !== key),
	})));

	const confirmName = nameForm.handleSubmit(({ title }) => {
		const clean = title.trim();
		if (clean.length === 0) return;
		setGroups(current => editing === null
			? [...current, { key: `new-${Date.now()}`, id: null, title: clean, keys: [] }]
			: current.map(g => g.key === editing ? { ...g, title: clean } : g));
		nameDialogue.close();
	});

	const titles = groups.map(g => g.title.trim().toLowerCase());
	const duplicate = titles.some((t, i) => titles.indexOf(t) !== i);
	const editingTitle = groups.find(g => g.key === editing)?.title ?? '';

	return <Drawer title="Channel groups" size="huge" ref={drawer.ref} onCloseRequest={requestClose}
		actions={<FormButton state="primary" isLoading={save.isPending} disabled={duplicate}
			onClick={() => save.mutate(groups.map(g => ({ id: g.id, title: g.title, keys: g.keys })))}>
			Save groups
		</FormButton>}>
		<div className="flex flex-col gap-4 px-4 py-6 sm:px-6">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<p className="text-sm text-gray-500">
					Drag each channel into a group. {channels.length} channels, {groups.length} groups.
				</p>
				<FormButton state="white" size="small" icon={<PlusIcon aria-hidden="true" className="h-4 w-4" />}
					onClick={() => { setEditing(null); nameForm.reset({ title: '' }); nameDialogue.open(null); }}>
					New group
				</FormButton>
			</div>
			<Message state="info" flush>
				The groups apply to every property of {companyName} ({propertyCount} properties). Names that differ only in capitals or spaces are one
				channel. The figures are the bookings made in the last 12 months, all stay dates, sold now, largest first.
				{savedDay === null ? ' The groups were never saved: no channel is marked “New” yet.'
					: ` “New” = a channel in no group whose first booking came after the last save (${formatDate(savedDay)})${newCount > 0 ? `: ${newCount} now` : ''}.`}
			</Message>
			{duplicate && <Message state="error" flush>Two groups have the same name. Rename one of them.</Message>}
			{changed && (data.groups.length === 0
				? <Message state="warning" title="Groups not saved" flush>The report shows each channel on its own until you click Save groups.</Message>
				: <Message state="warning" title="Changes not saved" flush>The report uses the saved groups until you click Save groups.</Message>)}
			<GroupBoard groups={board} ariaLabel="Channel groups" emptyLabel="Drag channels here" onMove={move} />
		</div>
		<Dialogue ref={nameDialogue.ref} title={editing === null ? 'New group' : 'Rename the group'} size="small"
			confirmButtonName={editing === null ? 'Add group' : 'Rename'} cancelButtonName="Cancel" isAutoClose={false}
			onConfirm={() => void confirmName()}>
			<FormInputText form={nameForm} name="title" label="Name" placeholder="For example Expedia" maxLength={40} isRequired />
		</Dialogue>
		<Dialogue ref={deleteDialogue.ref} title={`Delete the group “${editingTitle}”?`} size="small"
			confirmButtonName="Delete" cancelButtonName="Cancel"
			onConfirm={() => setGroups(current => current.filter(g => g.key !== editing))}>
			<p className="text-sm text-gray-500">Its channels go to “Not grouped”. Nothing changes before Save groups.</p>
		</Dialogue>
		<Dialogue ref={closeDialogue.ref} title="Close without saving?" size="small"
			confirmButtonName="Close without saving" cancelButtonName="Keep editing" onConfirm={() => drawer.close()}>
			<p className="text-sm text-gray-500">RoomPulse drops the changes to the groups. The report keeps the saved groups.</p>
		</Dialogue>
	</Drawer>;
});
ChannelGroupsDrawer.displayName = 'ChannelGroupsDrawer';

export default ChannelGroupsDrawer;
